import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { assignSpatialGridPlacements, type SpatialGridArtwork } from "../lib/galleryLayout";
import type { Artwork } from "../types/artwork";

interface GalleryRoomProps {
  artworks: Artwork[];
  selectedArtwork: Artwork | null;
  onSelectArtwork: (artwork: Artwork) => void;
}

type ArtworkMesh = THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial> & {
  userData: {
    artwork: SpatialGridArtwork<Artwork>;
    baseScale: number;
    planeAspect: number;
  };
};

const vertexShader = `
  uniform float uTime;
  uniform float uHover;
  uniform float uVelocity;
  varying vec2 vUv;

  void main() {
    vUv = uv;
    vec3 pos = position;
    float ripple = sin((uv.x * 7.0) + uTime) * cos((uv.y * 4.0) - uTime * 0.65);
    pos.z += ripple * 0.055 * uVelocity;
    pos.z += uHover * 0.08 * sin(uv.y * 3.14159);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
  }
`;

const fragmentShader = `
  uniform sampler2D uMap;
  uniform float uImageAspect;
  uniform float uPlaneAspect;
  uniform float uHover;
  varying vec2 vUv;

  vec2 coverUv(vec2 uv, float imageAspect, float planeAspect) {
    vec2 ratio = vec2(1.0);
    if (imageAspect > planeAspect) {
      ratio.x = planeAspect / imageAspect;
    } else {
      ratio.y = imageAspect / planeAspect;
    }
    return (uv - 0.5) * ratio + 0.5;
  }

  void main() {
    vec2 uv = coverUv(vUv, uImageAspect, uPlaneAspect);
    vec4 tex = texture2D(uMap, uv);
    float vignette = smoothstep(0.98, 0.28, distance(vUv, vec2(0.5)));
    tex.rgb *= mix(0.76, 1.06, vignette);
    tex.rgb += uHover * 0.08;
    gl_FragColor = vec4(tex.rgb, 1.0);
  }
`;

export function GalleryRoom({ artworks, selectedArtwork, onSelectArtwork }: GalleryRoomProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const onSelectRef = useRef(onSelectArtwork);
  const selectedIdRef = useRef(selectedArtwork?.id ?? null);
  const placedArtworks = useMemo(() => assignSpatialGridPlacements(artworks), [artworks]);

  useEffect(() => {
    onSelectRef.current = onSelectArtwork;
  }, [onSelectArtwork]);

  useEffect(() => {
    selectedIdRef.current = selectedArtwork?.id ?? null;
  }, [selectedArtwork?.id]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || placedArtworks.length === 0) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color("#080909");
    scene.fog = new THREE.Fog("#080909", 7, 17);

    const camera = new THREE.PerspectiveCamera(46, mount.clientWidth / mount.clientHeight, 0.1, 80);
    camera.position.set(0, 0.1, 8.2);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    mount.appendChild(renderer.domElement);

    const grid = new THREE.Group();
    grid.rotation.x = -0.05;
    scene.add(grid);

    const textureLoader = new THREE.TextureLoader();
    const meshes: ArtworkMesh[] = [];
    const textures: THREE.Texture[] = [];
    const rowCount = Math.max(1, Math.ceil(placedArtworks.length / 7));
    const wrapWidth = 21;
    const wrapHeight = Math.max(9.5, rowCount * 2.25);
    const pointer = new THREE.Vector2(0, 0);
    const raycaster = new THREE.Raycaster();
    const motion = {
      x: 0,
      y: 0,
      targetX: 0,
      targetY: 0,
      velocity: 0
    };
    const drag = {
      active: false,
      startX: 0,
      startY: 0,
      lastX: 0,
      lastY: 0,
      moved: false
    };

    for (const artwork of placedArtworks) {
      const planeAspect = (artwork.gridColumn + artwork.gridRow) % 3 === 0 ? 0.78 : 1.22;
      const height = planeAspect > 1 ? 1.18 : 1.58;
      const width = height * planeAspect;
      const material = createArtworkMaterial(createPlaceholderTexture(artwork.title), planeAspect);
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(width, height, 18, 12), material) as ArtworkMesh;
      mesh.userData.artwork = artwork;
      mesh.userData.baseScale = artwork.gridScale;
      mesh.userData.planeAspect = planeAspect;
      grid.add(mesh);
      meshes.push(mesh);
      textures.push(material.uniforms.uMap.value as THREE.Texture);

      if (artwork.imageUrl) {
        textureLoader.load(
          artwork.imageUrl,
          (texture) => {
            texture.colorSpace = THREE.SRGBColorSpace;
            texture.wrapS = THREE.ClampToEdgeWrapping;
            texture.wrapT = THREE.ClampToEdgeWrapping;
            material.uniforms.uMap.value = texture;
            material.uniforms.uImageAspect.value =
              texture.image && texture.image.height ? texture.image.width / texture.image.height : 1;
            material.needsUpdate = true;
            textures.push(texture);
          },
          undefined,
          () => {
            material.uniforms.uHover.value = 0.2;
          }
        );
      }
    }

    const setPointer = (event: PointerEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    };

    const handlePointerMove = (event: PointerEvent) => {
      setPointer(event);
      motion.targetX += event.movementX * 0.002;
      motion.targetY -= event.movementY * 0.001;

      if (drag.active) {
        const deltaX = event.clientX - drag.lastX;
        const deltaY = event.clientY - drag.lastY;
        motion.targetX += deltaX * 0.014;
        motion.targetY -= deltaY * 0.012;
        drag.lastX = event.clientX;
        drag.lastY = event.clientY;
        if (Math.abs(event.clientX - drag.startX) + Math.abs(event.clientY - drag.startY) > 8) {
          drag.moved = true;
        }
      }
    };

    const handlePointerDown = (event: PointerEvent) => {
      renderer.domElement.setPointerCapture(event.pointerId);
      setPointer(event);
      drag.active = true;
      drag.startX = event.clientX;
      drag.startY = event.clientY;
      drag.lastX = event.clientX;
      drag.lastY = event.clientY;
      drag.moved = false;
    };

    const handlePointerUp = (event: PointerEvent) => {
      renderer.domElement.releasePointerCapture(event.pointerId);
      drag.active = false;
      setPointer(event);
      raycaster.setFromCamera(pointer, camera);
      const hit = raycaster.intersectObjects(meshes, false)[0];
      if (!drag.moved && hit?.object.userData.artwork) {
        onSelectRef.current(hit.object.userData.artwork);
      }
    };

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      motion.targetY += event.deltaY * -0.006;
      motion.targetX += event.deltaX * -0.004;
      motion.velocity = Math.min(1, motion.velocity + Math.abs(event.deltaY) * 0.002);
    };

    const handleResize = () => {
      const width = mount.clientWidth;
      const height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };

    renderer.domElement.addEventListener("pointermove", handlePointerMove);
    renderer.domElement.addEventListener("pointerdown", handlePointerDown);
    renderer.domElement.addEventListener("pointerup", handlePointerUp);
    renderer.domElement.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("resize", handleResize);

    let hoverMesh: ArtworkMesh | null = null;
    let animationFrame = 0;
    const clock = new THREE.Clock();

    const animate = () => {
      const elapsed = clock.getElapsedTime();
      motion.x += (motion.targetX - motion.x) * 0.075;
      motion.y += (motion.targetY - motion.y) * 0.075;
      motion.velocity += (0 - motion.velocity) * 0.045;

      raycaster.setFromCamera(pointer, camera);
      hoverMesh = (raycaster.intersectObjects(meshes, false)[0]?.object as ArtworkMesh | undefined) ?? null;
      renderer.domElement.style.cursor = hoverMesh ? "pointer" : drag.active ? "grabbing" : "grab";

      for (const mesh of meshes) {
        const artwork = mesh.userData.artwork;
        const drift = Math.sin(elapsed * 0.28 + artwork.gridDrift * 8) * 0.12;
        const x = wrapCentered(artwork.gridX + motion.x + drift, wrapWidth);
        const y = wrapCentered(artwork.gridY + motion.y, wrapHeight);
        const xNormal = x / (wrapWidth * 0.5);
        const selected = selectedIdRef.current === artwork.id;
        const hovered = hoverMesh === mesh;
        const depth = artwork.gridZ - Math.abs(xNormal) * 1.2 + Math.sin(elapsed * 0.2 + artwork.gridRow) * 0.08;
        const targetScale = mesh.userData.baseScale * (selected ? 1.28 : hovered ? 1.16 : 1);

        mesh.position.set(x, y, depth);
        mesh.rotation.set(0.02 * y, artwork.gridRotationY - xNormal * 0.3, xNormal * -0.05);
        mesh.scale.lerp(new THREE.Vector3(targetScale, targetScale, targetScale), 0.12);
        mesh.material.uniforms.uTime.value = elapsed + artwork.gridColumn;
        mesh.material.uniforms.uHover.value += ((hovered || selected ? 1 : 0) - mesh.material.uniforms.uHover.value) * 0.12;
        mesh.material.uniforms.uVelocity.value +=
          (Math.min(1.4, Math.abs(motion.targetY - motion.y) * 0.12 + motion.velocity) -
            mesh.material.uniforms.uVelocity.value) *
          0.1;
      }

      camera.position.x += (pointer.x * 0.45 - camera.position.x) * 0.045;
      camera.position.y += (pointer.y * 0.25 - camera.position.y) * 0.045;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
      animationFrame = window.requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", handleResize);
      renderer.domElement.removeEventListener("pointermove", handlePointerMove);
      renderer.domElement.removeEventListener("pointerdown", handlePointerDown);
      renderer.domElement.removeEventListener("pointerup", handlePointerUp);
      renderer.domElement.removeEventListener("wheel", handleWheel);
      renderer.dispose();
      textures.forEach((texture) => texture.dispose());
      scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          object.geometry.dispose();
          const materials = Array.isArray(object.material) ? object.material : [object.material];
          materials.forEach((material) => material.dispose());
        }
      });
      mount.removeChild(renderer.domElement);
    };
  }, [placedArtworks]);

  return (
    <section className="gallery-hero" id="top" aria-label="Interactive painting grid">
      <div ref={mountRef} className="gallery-canvas" />
      <div className="hero-copy">
        <p className="eyebrow">Worcestershire based abstract painter</p>
        <h1>Jan Husband</h1>
        <p>Move through a living grid of abstract landscapes, select a painting, and enquire about available work.</p>
      </div>
      <div className="gallery-hint">Drag or scroll the grid. Select a painting for details.</div>
    </section>
  );
}

function createArtworkMaterial(texture: THREE.Texture, planeAspect: number) {
  return new THREE.ShaderMaterial({
    uniforms: {
      uMap: { value: texture },
      uImageAspect: { value: 1 },
      uPlaneAspect: { value: planeAspect },
      uTime: { value: 0 },
      uHover: { value: 0 },
      uVelocity: { value: 0 }
    },
    vertexShader,
    fragmentShader
  });
}

function createPlaceholderTexture(title: string) {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 512;
  const context = canvas.getContext("2d");
  if (context) {
    const gradient = context.createLinearGradient(0, 0, 512, 512);
    gradient.addColorStop(0, "#9b6a2d");
    gradient.addColorStop(0.46, "#172a25");
    gradient.addColorStop(1, "#d8c3a0");
    context.fillStyle = gradient;
    context.fillRect(0, 0, 512, 512);
    context.fillStyle = "rgba(0, 0, 0, 0.45)";
    context.fillRect(28, 28, 456, 456);
    context.fillStyle = "#fff8ea";
    context.font = "700 38px Georgia";
    context.textAlign = "center";
    context.textBaseline = "middle";
    wrapCanvasText(context, title, 256, 256, 360, 44);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function wrapCanvasText(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number
) {
  const words = text.split(" ");
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const testLine = line ? `${line} ${word}` : word;
    if (context.measureText(testLine).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = testLine;
    }
  }
  lines.push(line);
  const startY = y - ((lines.length - 1) * lineHeight) / 2;
  lines.forEach((lineText, index) => context.fillText(lineText, x, startY + index * lineHeight));
}

function wrapCentered(value: number, size: number) {
  return ((((value + size / 2) % size) + size) % size) - size / 2;
}
